import { useCallback, useEffect, useRef, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { checkoutCart } from "@/networking/checkout.api";
import { useRouter } from "next/router";
import { isApiError } from "@/lib/api-helpers";
import { checkoutRedirect } from "@/lib/checkout-redirect";
import { useTranslation } from "@/hooks/useTranslation";

interface BlikOtpModalProps {
  isOpen: boolean;
  cartToken: string;
  onClose: (error?: string) => void;
}

const OTP_LENGTH = 6;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30; // 60 seconds total

export default function BlikOtpModal({
  isOpen,
  cartToken,
  onClose,
}: BlikOtpModalProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const [otpCode, setOtpCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollAttemptsRef = useRef(0);
  const prevIsOpenRef = useRef(false);

  // Reset state when modal opens (using setTimeout to avoid synchronous setState in effect)
  useEffect(() => {
    let resetTimeout: number | undefined;

    if (isOpen && !prevIsOpenRef.current) {
      prevIsOpenRef.current = true;
      pollAttemptsRef.current = 0;
      resetTimeout = window.setTimeout(() => {
        setOtpCode("");
        setError(undefined);
        setIsSubmitting(false);
        setIsPolling(false);
        inputRef.current?.focus();
      }, 0);
    }
    if (!isOpen) {
      prevIsOpenRef.current = false;
    }

    return () => {
      if (resetTimeout) clearTimeout(resetTimeout);
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [isOpen]);

  // Polling effect — runs when isPolling becomes true
  useEffect(() => {
    if (!isPolling) return;

    let cancelled = false;

    async function poll() {
      if (cancelled) return;

      if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setIsPolling(false);
        onClose(t("blik_payment_timeout"));
        return;
      }

      pollAttemptsRef.current++;

      try {
        const res = await checkoutCart(cartToken);

        if (cancelled) return;

        if (res.order?.token || res.enrollment?.token || res.redirect_url) {
          setIsPolling(false);
          checkoutRedirect(res, cartToken);
          return;
        }
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn("BLIK poll:", err);
        }
      }

      if (!cancelled) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    }

    poll();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
    };
  }, [isPolling, cartToken, router, onClose, t]);

  const handleSubmit = useCallback(async () => {
    if (otpCode.length !== OTP_LENGTH) return;

    setIsSubmitting(true);
    setError(undefined);

    try {
      // Re-call checkout with the OTP included in payment_method_data.
      // The backend will create the PPRO agreement with the OTP in authenticationSettings.
      await checkoutCart(cartToken, {
        paymentMethodData: { blik_verification_code: otpCode },
      });

      // Agreement creation submitted — poll for order creation (via webhook)
      setIsSubmitting(false);
      pollAttemptsRef.current = 0;
      setIsPolling(true);
    } catch (err) {
      setIsSubmitting(false);

      if (isApiError(err)) {
        const errorMessage =
          err.data?.error_message ||
          err.data?.errors?.payment?.[0] ||
          t("blik_authorization_failed");
        setError(errorMessage);
      } else {
        setError(t("something_went_wrong"));
      }
    }
  }, [otpCode, cartToken, t]);

  const handleOtpChange = (value: string) => {
    // Only allow digits, max OTP_LENGTH characters
    const cleaned = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    setOtpCode(cleaned);
    setError(undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && otpCode.length === OTP_LENGTH && !isSubmitting) {
      handleSubmit();
    }
  };

  const handleCancel = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
    }
    setIsPolling(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("blik_enter_code")}</DialogTitle>
          <DialogDescription>
            {t("blik_enter_code_description")}
          </DialogDescription>
        </DialogHeader>

        {isPolling ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-t-4 border-gray-600 border-t-transparent" />
            <p className="text-sm text-gray-600">
              {t("blik_processing_payment")}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={OTP_LENGTH}
              value={otpCode}
              onChange={(e) => handleOtpChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="000000"
              disabled={isSubmitting}
              className="w-full rounded-md border px-4 py-3 text-center font-mono text-2xl tracking-[0.5em] focus:ring-2 focus:ring-gray-400 focus:outline-hidden disabled:opacity-50"
            />

            {error && (
              <p className="text-center text-sm text-red-600">{error}</p>
            )}

            <Button
              onClick={handleSubmit}
              disabled={otpCode.length !== OTP_LENGTH || isSubmitting}
              className="h-input w-full font-semibold"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent" />
                  {t("blik_verifying")}
                </div>
              ) : (
                t("blik_confirm_payment")
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
